import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixBookingsTimeslotsConfigurationV1TimeSlotsConfigurationServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    _: [
      {
        srcPath: '/_api/time-slots-config',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/time-slots-config',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/time-slots-config',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/time-slots-config',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/time-slots-config',
        destPath: '',
      },
    ],
    'apps._base_domain_': [
      {
        srcPath: '/_api/time-slots-config',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/time-slots-config',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_bookings_time-slots-configuration';

/** Retrieves a time slots configuration by ID. */
export function getTimeSlotsConfiguration(
  payload: object
): RequestOptionsFactory<any> {
  function __getTimeSlotsConfiguration({ host }: any) {
    const metadata = {
      entityFqdn:
        'wix.bookings.timeslots_configuration.v1.time_slots_configuration',
      method: 'GET' as any,
      methodFqn:
        'wix.bookings.timeslots.configuration.v1.TimeSlotsConfigurationService.GetTimeSlotsConfiguration',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBookingsTimeslotsConfigurationV1TimeSlotsConfigurationServiceUrl(
        {
          protoPath: '/v1/time-slots-configurations/{timeSlotsConfigurationId}',
          data: payload,
          host,
        }
      ),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'timeSlotsConfiguration.createdDate' },
              { path: 'timeSlotsConfiguration.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getTimeSlotsConfiguration;
}

/** Retrieves a time slots configuration by app ID. */
export function getTimeSlotsConfigurationByAppId(
  payload: object
): RequestOptionsFactory<any> {
  function __getTimeSlotsConfigurationByAppId({ host }: any) {
    const metadata = {
      entityFqdn:
        'wix.bookings.timeslots_configuration.v1.time_slots_configuration',
      method: 'GET' as any,
      methodFqn:
        'wix.bookings.timeslots.configuration.v1.TimeSlotsConfigurationService.GetTimeSlotsConfigurationByAppId',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBookingsTimeslotsConfigurationV1TimeSlotsConfigurationServiceUrl(
        {
          protoPath: '/v1/time-slots-configurations/app/{appId}',
          data: payload,
          host,
        }
      ),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'timeSlotsConfiguration.createdDate' },
              { path: 'timeSlotsConfiguration.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getTimeSlotsConfigurationByAppId;
}

/**
 * Updates a time slots configuration.
 *
 * Each time the time slots configuration is updated, `revision` increments by 1.
 * The current `revision` must be passed when updating the time slots configuration.
 * This ensures you're working with the latest time slots configuration and prevents unintended overwrites.
 */
export function updateTimeSlotsConfiguration(
  payload: object
): RequestOptionsFactory<any> {
  function __updateTimeSlotsConfiguration({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'timeSlotsConfiguration.createdDate' },
          { path: 'timeSlotsConfiguration.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn:
        'wix.bookings.timeslots_configuration.v1.time_slots_configuration',
      method: 'PATCH' as any,
      methodFqn:
        'wix.bookings.timeslots.configuration.v1.TimeSlotsConfigurationService.UpdateTimeSlotsConfiguration',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBookingsTimeslotsConfigurationV1TimeSlotsConfigurationServiceUrl(
        {
          protoPath:
            '/v1/time-slots-configurations/{timeSlotsConfiguration.id}',
          data: serializedData,
          host,
        }
      ),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'timeSlotsConfiguration.createdDate' },
              { path: 'timeSlotsConfiguration.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateTimeSlotsConfiguration;
}

/** Retrieves a list of time slots configurations, given the provided paging, filtering, and sorting. */
export function queryTimeSlotsConfigurations(
  payload: object
): RequestOptionsFactory<any> {
  function __queryTimeSlotsConfigurations({ host }: any) {
    const metadata = {
      entityFqdn:
        'wix.bookings.timeslots_configuration.v1.time_slots_configuration',
      method: 'GET' as any,
      methodFqn:
        'wix.bookings.timeslots.configuration.v1.TimeSlotsConfigurationService.QueryTimeSlotsConfigurations',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBookingsTimeslotsConfigurationV1TimeSlotsConfigurationServiceUrl(
        {
          protoPath: '/v1/time-slots-configurations/query',
          data: payload,
          host,
        }
      ),
      params: toURLSearchParams(payload, true),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'timeSlotsConfigurations.createdDate' },
              { path: 'timeSlotsConfigurations.updatedDate' },
            ],
          },
        ]),
      fallback: [
        {
          method: 'POST' as any,
          url: resolveWixBookingsTimeslotsConfigurationV1TimeSlotsConfigurationServiceUrl(
            {
              protoPath: '/v1/time-slots-configurations/query',
              data: payload,
              host,
            }
          ),
          data: payload,
        },
      ],
    };

    return metadata;
  }

  return __queryTimeSlotsConfigurations;
}
